import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('cart:updated', this.#handleGiftCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:updated', this.#handleGiftCartUpdate);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Fired by `gift-with-cart.js` after the gift product is added/removed.
   * Refreshes the cart items + bubble without opening the drawer.
   */
  #handleGiftCartUpdate = () => {
    this.#refreshCartItems();
  };

  /**
   * Re-render the cart items component inside the drawer, and update the cart bubble.
   * Uses the section rendering API so the drawer content matches the server state.
   */
  async #refreshCartItems() {
    const cartItemsComponent = this.querySelector('cart-items-component');
    const sectionId = cartItemsComponent?.dataset.sectionId;

    if (!sectionId) {
      // Fallback: reload the page so the cart reflects the latest state
      window.location.reload();
      return;
    }

    try {
      const url = `${window.Shopify.routes.root}?section_id=${sectionId}`;
      const response = await fetch(url);
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newCartItems = parsed.querySelector('cart-items-component');

      if (newCartItems && cartItemsComponent) {
        cartItemsComponent.replaceWith(newCartItems);
      }

      // Update the cart bubble / count elsewhere on the page
      this.#refreshCartBubble(parsed);
    } catch (error) {
      console.error('[cart-drawer] Failed to refresh cart items:', error);
    }
  }

  /**
   * Replaces any `cart-bubble` elements on the page with the freshly rendered ones.
   * This keeps the header cart count in sync without a full page reload.
   */
  #refreshCartBubble(parsedDoc) {
    const newBubbles = parsedDoc.querySelectorAll('cart-bubble');
    const currentBubbles = document.querySelectorAll('cart-bubble');

    if (newBubbles.length !== currentBubbles.length) {
      // Fallback: try to match by parent — most themes render one bubble in the header
      currentBubbles.forEach((current, i) => {
        if (newBubbles[i]) current.replaceWith(newBubbles[i]);
      });
      return;
    }

    currentBubbles.forEach((current, i) => {
      current.replaceWith(newBubbles[i]);
    });
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}